// What the signed-in person can do (CLIENT-ARCHITECTURE §5), computed from the
// API on sign-in. Navigation and actions read only from this; the API is
// always the authority, and a 403 triggers a refresh.
import { useQuery } from "@tanstack/react-query";
import { api, call } from "../api/client";
import { ApiError } from "../api/errors";
import { useSession } from "./useSession";

export type Permission =
  | "users.list"
  | "users.manage"
  | "entitlements.manage"
  | "applications.manage"
  | "roles.manage"
  | "organizations.manage"
  | "billing.manage"
  | "billing.refund"
  | "audit.view"
  | "webhooks.manage"
  | "api_keys.manage"
  | "tenants.manage"
  | "notifications.send"
  | "operations.view";

export interface Capabilities {
  userId: string;
  email: string;
  mfaEnabled: boolean;
  emailVerified: boolean;
  testMode: boolean;
  roleIds: string[];
  permissions: Set<string>;
  isSuperadmin: boolean;
  ownsApps: string[];
  /** Holds any platform permission: staff accounts must have two-factor on. */
  isStaff: boolean;
}

export function can(caps: Capabilities | undefined, permission: Permission): boolean {
  return Boolean(caps?.permissions.has(permission));
}

async function load(): Promise<Capabilities> {
  const [me, roles, owned] = await Promise.all([
    call(api.GET("/users/{id}", { params: { path: { id: "me" } } })),
    call(api.GET("/users/{id}/roles", { params: { path: { id: "me" }, query: { limit: 100 } } })),
    call(api.GET("/applications", { params: { query: { owned: true, limit: 100 } } })).catch(() => ({ data: [] as { id?: string }[] })),
  ]);
  const roleIds = (roles.data ?? []).filter((r) => !r.application_id).flatMap((r) => (r.role_id ? [r.role_id] : []));
  // Role definitions need users.list; without it there are no admin permissions to find.
  const platformRoles = await call(api.GET("/roles", { params: { query: { scope: "platform", limit: 100 } } })).catch((err) => {
    if (err instanceof ApiError && err.status === 403) return { data: [] as { id?: string; permissions?: string[] }[] };
    throw err;
  });
  const permissions = new Set<string>();
  for (const role of platformRoles.data ?? []) if (role.id && roleIds.includes(role.id)) role.permissions?.forEach((p) => permissions.add(p));
  const isSuperadmin = roleIds.includes("role_platform_superadmin");
  // Until the API has operations.view (API issue #47), Operations is superadmin-only.
  if (isSuperadmin) permissions.add("operations.view");
  return {
    userId: me.id ?? "",
    email: me.email ?? "",
    mfaEnabled: me.mfa_enabled ?? false,
    emailVerified: me.email_verified ?? false,
    testMode: me.test_mode ?? false,
    roleIds,
    permissions,
    isSuperadmin,
    ownsApps: (owned.data ?? []).flatMap((a) => (a.id ? [a.id] : [])),
    isStaff: permissions.size > 0,
  };
}

export function useCapabilities() {
  const { userId } = useSession();
  return useQuery({ queryKey: ["capabilities", userId], queryFn: load, enabled: Boolean(userId), staleTime: Infinity, refetchOnWindowFocus: true });
}
